/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import PageMeta from '../../components/shared/PageMeta';
import { SITE } from '../../config/site';
import agentsRegistry from '../../data/agents.json';
import './agentLanding.css';

// Per-agent capability page. DERIVED from src/data/agents.json (fetched from
// the central instance, never hand-authored) and reached at /agents/:slug/about
// -- a sub-path, so the existing /agents/:agentName chat route is untouched.
//
// The proof is live, not claimed: the same anonymous embeddable agent the
// widget docs describe (/agents/<Name>?plugin=1) is embedded on the page, so a
// visitor talks to the real agent here. That is the honest version of Explee's
// "watch it work" -- there is nothing to fabricate because the agent runs.

function useReveal() {
  // Explee-style scroll reveal: elements with .reveal fade+rise into view as
  // they enter the viewport. IntersectionObserver, no dependency. Prerender
  // and no-JS crawlers see the content already (CSS only hides under JS).
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    document.documentElement.classList.add('js-reveal');
    const els = document.querySelectorAll('.reveal');
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          io.unobserve(e.target);
        }
      }
    }, { threshold: 0.14, rootMargin: '0px 0px -8% 0px' });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

function AgentLanding() {
  const { agentName } = useParams();
  const agent = (agentsRegistry.agents || []).find((a) => a.slug === agentName) || null;
  useReveal();
  const [chatOpen, setChatOpen] = useState(false);

  if (!agent) return <Navigate to="/agents" replace />;

  const url = `${SITE}/agents/${agent.slug}/about`;
  // The live embed and the "Talk" CTA target the EXISTING chat route, using
  // the SAME name→path transform the gallery's "Talk To Agent" button uses
  // (spaces→dashes), so the agent resolves identically and nothing here
  // re-implements or regresses chat.
  const chatBase = `/agents/${agent.name.replace(/\s+/g, '-')}`;
  const embedSrc = `${chatBase}?plugin=1&auto_guest=1`;
  const capability = agent.capability || agent.tagline || '';

  // A capability sentence, cleaned up for the hero. Registry prompts are
  // instructions ("Tell the current updates from greenland"); shown as-is they
  // read oddly, so present them as what the agent does.
  const doesWhat = capability
    ? capability.charAt(0).toUpperCase() + capability.slice(1)
    : `Talk to ${agent.name}, a self-evolving Hevolve agent.`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: `${agent.name} — Hevolve AI agent`,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web, Windows, macOS, Linux, Android',
    description: doesWhat,
    url,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    publisher: { '@type': 'Organization', name: 'Hevolve AI', url: SITE },
    ...(agent.image ? { image: agent.image } : {}),
  };

  return (
    <div className="agent-landing">
      <PageMeta
        title={`${agent.name}: what this AI agent can do | Hevolve AI`}
        description={doesWhat.slice(0, 155)}
        ogTitle={`${agent.name} — a Hevolve AI agent you can try now`}
        ogDescription={doesWhat.slice(0, 155)}
        path={url}
        type="website"
      >
        {agent.image && <meta property="og:image" content={agent.image} />}
        {agent.image && <meta name="twitter:image" content={agent.image} />}
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Agents', item: `${SITE}/agents` },
            { '@type': 'ListItem', position: 2, name: agent.name, item: url },
          ],
        })}</script>
      </PageMeta>

      {/* ── minimal top bar: logo + back, NOT the heavy site header ── */}
      <header className="al-topbar">
        <Link to="/" className="al-brand">HEVOLVE</Link>
        <nav className="al-topnav">
          <Link to="/agents">← All agents</Link>
          <a className="al-btn al-btn-sm" href={chatBase}>Talk to {agent.name}</a>
        </nav>
      </header>

      {/* ── hero ── */}
      <section className="al-hero">
        <div className="al-hero-inner">
          <div className="al-hero-copy reveal">
            <span className="al-eyebrow">Hevolve AI agent</span>
            <h1 className="al-title">{agent.name}</h1>
            <p className="al-tagline">{doesWhat}</p>
            <div className="al-cta-row">
              <button className="al-btn al-btn-primary" onClick={() => setChatOpen(true)}>
                ▶ See it work
              </button>
              <a className="al-btn al-btn-ghost" href={chatBase}>Open full chat</a>
            </div>
            <p className="al-microcopy">Runs live in your browser — no signup, no API key.</p>
          </div>
          <div className="al-hero-art reveal">
            {agent.image ? (
              <img src={agent.image} alt={agent.name} loading="eager"
                   width="360" height="360" className="al-avatar" />
            ) : (
              <div className="al-avatar al-avatar-fallback">{agent.name.slice(0, 1)}</div>
            )}
            <div className="al-orbit" aria-hidden="true" />
          </div>
        </div>
      </section>

      {/* ── live proof: the real agent, embedded ── */}
      <section className="al-section al-proof reveal" id="try">
        <h2 className="al-h2">Try {agent.name}, right here</h2>
        <p className="al-lead">
          This is the actual agent, running live. Ask it something and watch it
          respond — the same one you would get in Nunba or on any page you embed
          it in.
        </p>
        <div className="al-embed-frame">
          {chatOpen ? (
            <iframe
              title={`${agent.name} live`}
              src={embedSrc}
              className="al-embed"
              allow="microphone; autoplay"
              loading="lazy"
            />
          ) : (
            <button className="al-embed-poster" onClick={() => setChatOpen(true)}>
              {agent.image && <img src={agent.image} alt="" aria-hidden="true" />}
              <span className="al-embed-play">▶</span>
              <span className="al-embed-label">Tap to talk to {agent.name}</span>
            </button>
          )}
        </div>
      </section>

      {/* ── what it does: Explee-style sticky-stacking cards. Each card pins
             at a stepped offset so they pile up as you scroll, the previous
             peeking at the top. Pure CSS position:sticky, no library. ── */}
      <section className="al-stack">
        <h2 className="al-h2 al-stack-title">What you get with {agent.name}</h2>
        <div className="al-stack-deck">
          {[
            { t: 'What it does', d: doesWhat, tag: 'capability' },
            { t: 'Runs anywhere', d: `Talk to ${agent.name} on the web, or inside Nunba on Windows, macOS, Linux and Android. The same agent on every device.`, tag: 'everywhere' },
            { t: 'Yours to embed', d: `Drop one iframe on any page and ${agent.name} answers your visitors, anonymous and one click, no login.`, tag: 'embeddable' },
            { t: 'Free to try', d: `Runs live in your browser right now with a guest session. No signup, no API key, no card.`, tag: 'free' },
          ].map((c, i) => (
            <article className="al-stack-card" key={i}
                     style={{ top: `${90 + i * 26}px`, zIndex: i + 1 }}>
              <div className="al-stack-num">{String(i + 1).padStart(2, '0')}</div>
              <div className="al-stack-body">
                <span className="al-stack-tag">{c.tag}</span>
                <h3>{c.t}</h3>
                <p>{c.d}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ── closing CTA ── */}
      <section className="al-section al-final reveal">
        <h2 className="al-h2">Put {agent.name} to work</h2>
        <div className="al-cta-row al-cta-center">
          <a className="al-btn al-btn-primary" href={chatBase}>Talk to {agent.name}</a>
          <Link className="al-btn al-btn-ghost" to="/download">Get Nunba</Link>
        </div>
        <Link className="al-back" to="/agents">Browse all Hevolve agents →</Link>
      </section>
    </div>
  );
}

export default AgentLanding;
